import { useEffect, useRef, useState } from 'react';

import { glacierAssistantApiChat } from '@/lib/heyapi/sdk.gen';
import type { MessageSchema } from '@/lib/heyapi/types.gen';

import { ChatMessage } from './ChatMessage';
import { useChatStore } from './useChatStore';

class RateLimitError extends Error {
  constructor() {
    super('rate-limited');
    this.name = 'RateLimitError';
  }
}

async function sendMessage(
  messages: MessageSchema[],
  currentUrl: string
): Promise<{ response: string; interactionId: number }> {
  const { data, error, response } = await glacierAssistantApiChat({
    body: { messages, currentUrl },
  });

  if (response?.status === 429) {
    throw new RateLimitError();
  }
  if (error || !data) {
    throw new Error(`Chat request failed: ${response?.status}`);
  }

  return { response: data.response, interactionId: data.interaction_id };
}

export function ChatPanel() {
  const {
    isOpen,
    isExpanded,
    messages,
    isLoading,
    toggleOpen,
    toggleExpanded,
    addMessage,
    setLoading,
    clearMessages,
  } = useChatStore();
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;

    const userMessage = { role: 'user' as const, content: trimmed };
    addMessage(userMessage);
    setInput('');
    setLoading(true);

    try {
      const allMessages: MessageSchema[] = [...messages, userMessage].map(
        (m) => ({ role: m.role, content: m.content })
      );
      const { response, interactionId } = await sendMessage(
        allMessages,
        window.location.href
      );
      addMessage({
        role: 'assistant',
        content: response,
        interactionId,
      });
    } catch (err) {
      const content =
        err instanceof RateLimitError
          ? "You've sent a lot of messages in a short time. Please wait a minute before asking again."
          : 'Sorry, something went wrong. Please try again.';
      addMessage({
        role: 'assistant',
        content,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) return null;

  const panelSize = isExpanded ? 'h-[80vh] w-[700px]' : 'h-[500px] w-[400px]';

  return (
    <div
      className={`fixed bottom-36 right-6 z-50 flex ${panelSize} flex-col overflow-hidden rounded-lg border bg-white shadow-xl transition-all duration-200`}
    >
      {/* Header */}
      <div className="flex items-center justify-between bg-emerald-900 px-4 py-3 text-white">
        <span className="text-sm font-semibold">Aspen AI Assistant</span>
        <div className="flex items-center gap-3">
          {/* Expand/Collapse button */}
          <button
            onClick={toggleExpanded}
            className="text-white hover:text-gray-300"
            aria-label={isExpanded ? 'Shrink chat' : 'Expand chat'}
            title={isExpanded ? 'Shrink' : 'Expand'}
          >
            {isExpanded ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="4 14 10 14 10 20" />
                <polyline points="20 10 14 10 14 4" />
                <line x1="14" y1="10" x2="21" y2="3" />
                <line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 3 21 3 21 9" />
                <polyline points="9 21 3 21 3 15" />
                <line x1="21" y1="3" x2="14" y2="10" />
                <line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            )}
          </button>
          {/* Clear conversation button */}
          <button
            onClick={clearMessages}
            className="text-white hover:text-gray-300"
            aria-label="Clear conversation"
            title="Clear conversation"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 6h18" />
              <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
            </svg>
          </button>
          {/* Minimize button */}
          <button
            onClick={toggleOpen}
            className="text-white hover:text-gray-300"
            aria-label="Minimize chat"
            title="Minimize"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
          {/* Close button */}
          <button
            onClick={toggleOpen}
            className="text-white hover:text-gray-300"
            aria-label="Close chat"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3">
        {messages.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-gray-400">
            Ask me anything about the Aspen platform!
          </div>
        ) : null}
        {messages.map((msg) => (
          <ChatMessage key={msg.id} message={msg} />
        ))}
        {isLoading ? (
          <div className="mb-3 flex justify-start">
            <div className="rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-500">
              Thinking...
            </div>
          </div>
        ) : null}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t px-4 py-3">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask a question..."
            className="flex-1 rounded-md border px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            disabled={isLoading}
          />
          <button
            onClick={handleSend}
            disabled={isLoading || !input.trim()}
            className="rounded-md bg-emerald-700 px-3 py-2 text-sm text-white hover:bg-emerald-800 disabled:opacity-50"
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
}
